import { PointCloud } from "@/components/laser-engraving/PointCloud";
import { CrystalUtils } from "@/components/product/CrystalSelector";
import { useCrystalCustomizer } from "@/contexts/CrystalCustomizerContext";
import { useTextToPointCloud } from "@/hooks/product/useTextToPointCloud";
import { DEFAULT_POINT_CLOUD_CONFIG } from "@/lib/laser-physics";
import { useCallback, useEffect, useRef } from "react";
import { Crystal, MaterialType } from "./Crystal";

export interface TextPointCloudObjectProps {
  /** 水晶材质类型 */
  materialType?: MaterialType;
}

/**
 * 统一文本点云配置 - 集中管理所有参数
 */
const TEXT_POINT_CLOUD_CONFIG = {
  // Canvas渲染配置
  canvasWidth: 400,
  canvasHeight: 200,
  fontSize: 48,
  fontWeight: "bold",

  // 点云生成配置
  pointDensity: 8, // 适中密度，平衡效果与性能
  depthScale: 0.2,

  // 点云渲染配置
  pointSize: 0.015, // 清晰可见的点尺寸
  sizeMultiplier: 1.0,
  opacity: 1.0,
} as const;

export function TextPointCloudObject({
  materialType = "hybrid",
}: TextPointCloudObjectProps = {}) {
  const { config, setPointCloudGeometry, setCrystalDimensions } =
    useCrystalCustomizer();
  const {
    crystalType,
    transform,
    pointCloudGeometry,
    text,
    selectedFont,
    renderMode,
    crystalDimensions,
  } = config;

  // 防抖timer引用
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 文本转点云Hook - 使用统一配置
  const textToPointCloudResult = useTextToPointCloud(
    {
      text,
      fontFamily: selectedFont,
      fontSize: TEXT_POINT_CLOUD_CONFIG.fontSize,
      fontWeight: TEXT_POINT_CLOUD_CONFIG.fontWeight,
      pointDensity: TEXT_POINT_CLOUD_CONFIG.pointDensity,
      pointSize: TEXT_POINT_CLOUD_CONFIG.pointSize,
      canvasWidth: TEXT_POINT_CLOUD_CONFIG.canvasWidth,
      canvasHeight: TEXT_POINT_CLOUD_CONFIG.canvasHeight,
      depthScale: TEXT_POINT_CLOUD_CONFIG.depthScale,
    },
    crystalDimensions, // 🔗 传递实际水晶尺寸信息
  );

  // 自动触发点云生成 - 等待有效水晶尺寸后才生成
  useEffect(() => {
    // 文本为空时不生成
    if (!text.trim()) return;

    // 关键检查：只有在水晶boundingbox真正计算完成后才生成
    // scaleRatio > 1.0 说明已经从真实模型计算过，不是默认占位符值
    if (crystalDimensions.scaleRatio <= 1.0) {
      return;
    }

    // 清除之前的防抖timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // 对文本输入进行防抖处理 - 500ms延迟
    debounceTimerRef.current = setTimeout(() => {
      textToPointCloudResult.generatePointCloud();
    }, 500);

    // 清理函数
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [text, selectedFont, renderMode, crystalDimensions]); // 依赖crystalDimensions，但只在有效时才生成

  // 当点云生成完成时，自动更新Context
  useEffect(() => {
    const geometry = textToPointCloudResult.pointCloud.geometry;
    if (geometry) {
      setPointCloudGeometry(geometry);
    }
  }, [textToPointCloudResult.pointCloud.geometry, setPointCloudGeometry]);

  // 清理资源
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      textToPointCloudResult.cleanup();
    };
  }, [textToPointCloudResult.cleanup]);

  // 获取选中的水晶选项和modelPath
  const crystalOption = CrystalUtils.getOption(crystalType);
  if (!crystalOption) return null;

  // 水晶尺寸回调处理函数
  const handleDimensionsCalculated = useCallback(
    (dimensions: {
      width: number;
      height: number;
      depth: number;
      maxDimension: number;
      scaleRatio: number;
    }) => {
      setCrystalDimensions(dimensions);
    },
    [setCrystalDimensions],
  );

  const pointCloudConfig = {
    ...DEFAULT_POINT_CLOUD_CONFIG,
    showPointCloud: Boolean(pointCloudGeometry),
    pointSize: TEXT_POINT_CLOUD_CONFIG.pointSize,
    sizeMultiplier: TEXT_POINT_CLOUD_CONFIG.sizeMultiplier,
    opacity: TEXT_POINT_CLOUD_CONFIG.opacity,
  };

  const position: [number, number, number] = [
    transform.positionX,
    transform.positionY,
    transform.positionZ,
  ];
  const scale: [number, number, number] = [
    transform.scaleX,
    transform.scaleY,
    transform.scaleZ,
  ];

  return (
    <group position={position} scale={scale}>
      {/* 水晶外壳 - 始终显示 */}
      <Crystal
        modelPath={crystalOption.modelPath}
        materialType={materialType}
        targetSize={1.5}
        castShadow={true}
        receiveShadow={true}
        onDimensionsCalculated={handleDimensionsCalculated} // 🔗 传递尺寸回调
      />

      {/* 点云 - 从Context获取几何体 */}
      {pointCloudGeometry && (
        <PointCloud
          geometry={pointCloudGeometry}
          config={pointCloudConfig}
          visible={true}
        />
      )}
    </group>
  );
}
